import React from 'react';
import { HiArrowUpRight } from 'react-icons/hi2';
import StatusBadge from './StatusBadge';
import PlatformIcons from './PlatformIcons';

const ProjectCard = ({ project, onOpen, index }) => {
  const { name, tagline, image, category, status, year, techStack = [], platforms = [] } = project;

  return (
    <button
      onClick={() => onOpen(project)}
      style={{ animationDelay: `${Math.min(index * 60, 600)}ms` }}
      className="group relative flex animate-slide-up flex-col overflow-hidden rounded-2xl bg-white text-left opacity-0 shadow-card ring-1 ring-primary-100 transition-all duration-300 hover:-translate-y-1 hover:shadow-card-hover hover:ring-primary-200"
    >
      {/* Cover */}
      <div className="relative h-40 overflow-hidden bg-gradient-to-br from-primary-100 to-accent-100 sm:h-44">
        <img
          src={image}
          alt={name}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-neutral-dark/70 via-neutral-dark/10 to-transparent" />

        {/* Top row: status + year */}
        <div className="absolute left-3 right-3 top-3 flex items-start justify-between">
          <StatusBadge status={status} />
          {year && (
            <span className="rounded-full bg-white/15 px-2 py-0.5 text-[10px] font-medium text-white ring-1 ring-white/25 backdrop-blur-md">
              {year}
            </span>
          )}
        </div>

        {/* Bottom row: category */}
        <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
          <span className="rounded-md bg-primary-600/70 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white backdrop-blur-sm">
            {category}
          </span>
          <PlatformIcons platforms={platforms} />
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col p-4">
        <h3 className="text-base font-semibold text-neutral-dark transition-colors group-hover:text-primary-600">
          {name}
        </h3>
        <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-neutral">{tagline}</p>

        {techStack.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {techStack.slice(0, 4).map((t) => (
              <span
                key={t}
                className="rounded-full bg-primary-50 px-2 py-0.5 text-[10px] font-medium text-primary-700 ring-1 ring-primary-100"
              >
                {t}
              </span>
            ))}
            {techStack.length > 4 && (
              <span className="rounded-full px-2 py-0.5 text-[10px] font-medium text-neutral">
                +{techStack.length - 4}
              </span>
            )}
          </div>
        )}

        <div className="mt-4 flex items-center justify-between border-t border-primary-50 pt-3">
          <span className="text-[11px] font-medium text-neutral">Click to inspect</span>
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary-600 transition-all group-hover:gap-2">
            Open
            <HiArrowUpRight className="h-3.5 w-3.5" />
          </span>
        </div>
      </div>

      {/* Accent glow on hover */}
      <span
        aria-hidden
        className="pointer-events-none absolute -inset-px rounded-2xl opacity-0 shadow-glow-accent transition-opacity duration-300 group-hover:opacity-100"
      />
    </button>
  );
};

export default ProjectCard;
